import './Chatbot.module.css';

import { useUserState } from '@common/context/userContext';
import {
    applyMessengerBranding,
    buildChatUserMetadata,
    hasMessengerBootstrapped,
    IMessengerBranding,
    INGENIOUS_INTEGRATION_ID,
    isChatbotHiddenPath,
    markMessengerBootstrapped,
    resetMessengerBootstrap,
    setMessengerVisibility,
    waitForMessengerDocument,
} from '@utils/ingenious';
import MLALogger from '@utils/logger';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const BUSINESS_NAME = 'Integrity Systems';
const BRAND_COLOUR_HEX = '004976';
const INTRODUCTION_TEXT = 'How can I help you today?';

const BUSINESS_ICON_URL =
    'https://media.au.ingenious.ai/PNzhMleJh4R78R%2FFlVGPBgKlJfeJK%2FIS-logo-white-square.png?t=a6fbe17cc6dc7dd00f970d7651af83f2';

const BRANDING: IMessengerBranding = {
    buttonLabel: 'Chat with us',
    buttonIconUrl:
        'https://media.au.ingenious.ai/PNzhMleJh4R78R%2FFlVGPBgKlJfeJK%2Fics%20bot%20icon%20white.png?t=b8b182aa031c376127683f99dd48d5c6',
    // NOTE: supplied by ISC as an oraclecms *test* host - swap for the production asset when available.
    appIconUrl: 'https://isctest.oraclecms.com/images/noun-chat-5473855.png',
    brandColor: `#${BRAND_COLOUR_HEX}`,
    hoverColor: '#0069a9',
};

/**
 * Mounts the Ingenious AI "Chat with us" web messenger.
 *
 * Because eNVD is a SPA, the vendor's page-load behaviour is re-run on every route change: the
 * signed-in customer's identity and current URL are pushed to the messenger so the bot always has
 * up-to-date context.
 *
 * The widget is hidden on signed-out routes.
 */
const Chatbot: React.FC = () => {
    const { pathname, search } = useLocation();
    const [{ user }] = useUserState();
    const [isReady, setIsReady] = useState(false);

    const isHidden = isChatbotHiddenPath(pathname);

    useEffect(() => {
        if (isHidden || hasMessengerBootstrapped()) {
            return;
        }

        markMessengerBootstrapped();
        let isMounted = true;

        const bootstrap = async (): Promise<void> => {
            // Defined by ISC's loader snippet in index.html, which runs before React mounts.
            const messenger = window.ingeniousai;
            if (!messenger) {
                throw new Error('The Ingenious loader snippet did not run');
            }

            // Registered before init() so the loader stub queues it for the real messenger.
            messenger.on('widget:opened', () => {
                if (window.ingeniousai?.getConversations().length === 0) {
                    window.ingeniousai.createConversation({ displayName: BUSINESS_NAME, description: '' });
                }
            });

            await messenger.init({
                integrationId: INGENIOUS_INTEGRATION_ID,
                businessName: BUSINESS_NAME,
                displayName: BUSINESS_NAME,
                businessIconUrl: BUSINESS_ICON_URL,
                buttonIconUrl: '',
                buttonWidth: '180px',
                buttonHeight: '46px',
                browserStorage: 'localStorage',
                canUserSeeConversationList: false,
                customText: {
                    headerText: INTRODUCTION_TEXT,
                    inputPlaceholder: 'Type a message...',
                    sendButtonText: 'Send',
                    introductionText: INTRODUCTION_TEXT,
                },
                menuItems: {
                    imageUpload: false,
                    fileUpload: false,
                    shareLocation: false,
                },
                customColors: {
                    brandColor: BRAND_COLOUR_HEX,
                    conversationColor: BRAND_COLOUR_HEX,
                    actionColor: BRAND_COLOUR_HEX,
                },
            });

            const messengerDocument = await waitForMessengerDocument();
            if (messengerDocument) {
                applyMessengerBranding(messengerDocument, BRANDING);
            }

            if (isMounted) {
                setIsReady(true);
            }
        };

        bootstrap().catch((error) => {
            // Allow a later route change to retry - the chatbot is never allowed to break the app.
            resetMessengerBootstrap();
            // Not console.error directly: babel strips all console calls from production
            // builds, so the New Relic report inside MLALogger is the only signal that survives.
            MLALogger.Log(['Chatbot'], { message: 'Unable to start the ISC chatbot', error }, 'error');
        });

        return () => {
            isMounted = false;
        };
    }, [isHidden]);

    // Signed-out routes hide the widget; the customer may reach them without a page reload.
    useEffect(() => {
        setMessengerVisibility(!isHidden);
    }, [isHidden, isReady]);

    useEffect(() => {
        if (isHidden || !isReady || !window.ingeniousai) {
            return;
        }

        // Re-sent whenever the account changes too, so switching PIC is reflected in the conversation.
        window.ingeniousai
            .updateUser({ metadata: buildChatUserMetadata(user, window.location.href) })
            .catch(() => undefined);
    }, [isHidden, isReady, pathname, search, user]);

    return null;
};

export default Chatbot;
